import  { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  FileUpload as UploadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  DeleteForever as DeleteIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  CheckCircleOutline as ApproveIcon,
  HighlightOff as RejectIcon
} from '@mui/icons-material';
import Layout from '../components/layout/Calayout';

const FileLifecyclePage = () => {
  // File state management
  const [files, setFiles] = useState([]);
  
  // New file dialog state
  const [newFileDialogOpen, setNewFileDialogOpen] = useState(false);
  const [newFile, setNewFile] = useState({
    name: '',
    type: '',
    description: ''
  });

  // Edit file dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingFile, setEditingFile] = useState(null);

  // Add new file
  const handleAddFile = () => {
    const fileToAdd = {
      ...newFile,
      id: `file-${Date.now()}`,
      uploadDate: new Date().toLocaleString(),
      status: 'uploaded',
      lifecycle: [
        {
          stage: 'Uploaded',
          date: new Date().toLocaleString(),
          user: 'Current User'
        }
      ]
    };

    setFiles(prev => [...prev, fileToAdd]);
    setNewFileDialogOpen(false);
    // Reset new file form
    setNewFile({
      name: '',
      type: '',
      description: ''
    });
  };

  // Edit file details
  const handleEditFile = () => {
    setFiles(prev => prev.map(file => 
      file.id === editingFile.id 
        ? {
            ...file, 
            ...editingFile,
            lifecycle: [
              ...file.lifecycle,
              {
                stage: 'Edited',
                date: new Date().toLocaleString(),
                user: 'Current User'
              }
            ]
          }
        : file
    ));
    setEditDialogOpen(false);
  };

  // Update file status
  const updateFileStatus = (fileId, newStatus) => {
    setFiles(prev => prev.map(file => 
      file.id === fileId 
        ? {
            ...file, 
            status: newStatus,
            lifecycle: [
              ...file.lifecycle,
              {
                stage: newStatus.charAt(0).toUpperCase() + newStatus.slice(1),
                date: new Date().toLocaleString(),
                user: 'Current User'
              }
            ]
          }
        : file
    ));
  };

  // Render file status chip
  const renderStatusChip = (status) => {
    switch (status) {
      case 'uploaded':
        return <Chip label="Uploaded" color="default" />;
      case 'in_review':
        return <Chip label="In Review" color="warning" />;
      case 'approved':
        return <Chip label="Approved" color="success" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" />;
      case 'archived':
        return <Chip label="Archived" color="secondary" />;
      case 'deleted':
        return <Chip label="Deleted" color="error" variant="outlined" />;
      default:
        return <Chip label={status} />;
    }
  };

  // Render file lifecycle
  const renderFileLifecycle = (file) => (
    <Box mt={2}>
      <Stepper alternativeLabel>
        {file.lifecycle.map((stage, index) => (
          <Step key={index} completed={index < file.lifecycle.length - 1}>
            <StepLabel
              StepIconProps={{
                style: {
                  color: 
                    stage.stage === 'Uploaded' ? 'gray' :
                    stage.stage === 'Edited' ? 'purple' :
                    stage.stage === 'Approved' ? 'green' :
                    stage.stage === 'Rejected' ? 'red' :
                    stage.stage === 'Archived' ? 'orange' :
                    'blue'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle2">{stage.stage}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {stage.date} by {stage.user}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  return (
    <Layout>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">File Lifecycle Management</Typography>
            <Button 
              variant="contained" 
              startIcon={<UploadIcon />}
              onClick={() => setNewFileDialogOpen(true)}
            >
              Add New File
            </Button>
          </Box>

          {/* File List */}
          {files.map((file) => (
            <Paper key={file.id} elevation={3} sx={{ mb: 2, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle1">{file.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {file.type} | Uploaded: {file.uploadDate}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2">{file.description}</Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  {renderStatusChip(file.status)}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="flex" justifyContent="space-between">
                    <Tooltip title="View Details">
                      <IconButton 
                        color="primary"
                        onClick={() => {
                          setEditingFile(file);
                          setEditDialogOpen(true);
                        }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        color="secondary"
                        onClick={() => {
                          setEditingFile(file);
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {file.status !== 'in_review' && (
                      <Tooltip title="Send for Review">
                        <Button 
                          variant="outlined" 
                          color="warning"
                          size="small"
                          onClick={() => updateFileStatus(file.id, 'in_review')}
                        >
                          Review
                        </Button>
                      </Tooltip>
                    )}
                    {file.status === 'in_review' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton 
                            color="success"
                            onClick={() => updateFileStatus(file.id, 'approved')}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton 
                            color="error"
                            onClick={() => updateFileStatus(file.id, 'rejected')}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {file.status !== 'archived' ? (
                      <Tooltip title="Archive">
                        <IconButton 
                          color="secondary"
                          onClick={() => updateFileStatus(file.id, 'archived')}
                        >
                          <ArchiveIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Restore">
                        <IconButton 
                          color="primary"
                          onClick={() => updateFileStatus(file.id, 'uploaded')}
                        >
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error"
                        onClick={() => updateFileStatus(file.id, 'deleted')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>

              {/* File Lifecycle Stepper */}
              {renderFileLifecycle(file)}
            </Paper>
          ))}

          {/* New File Dialog */}
          <Dialog
            open={newFileDialogOpen}
            onClose={() => setNewFileDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Add New File</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="File Name"
                    variant="outlined"
                    value={newFile.name}
                    onChange={(e) => setNewFile(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>File Type</InputLabel>
                    <Select
                      value={newFile.type}
                      onChange={(e) => setNewFile(prev => ({
                        ...prev,
                        type: e.target.value
                      }))}
                      label="File Type"
                      required
                    >
                      <MenuItem value="Document">Document</MenuItem>
                      <MenuItem value="Spreadsheet">Spreadsheet</MenuItem>
                      <MenuItem value="Presentation">Presentation</MenuItem>
                      <MenuItem value="Image">Image</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={newFile.description}
                    onChange={(e) => setNewFile(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNewFileDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAddFile}
                disabled={!newFile.name || !newFile.type}
              >
                Add File
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit File Dialog */}
          <Dialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>File Details</DialogTitle>
            <DialogContent>
              {editingFile && (
                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="File Name"
                      variant="outlined"
                      value={editingFile.name}
                      onChange={(e) => setEditingFile(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="File Type"
                      variant="outlined"
                      value={editingFile.type}
                      onChange={(e) => setEditingFile(prev => ({
                        ...prev,
                        type: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      variant="outlined"
                      multiline
                      rows={3}
                      value={editingFile.description}
                      onChange={(e) => setEditingFile(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h6">File Metadata</Typography>
                    <Typography>Upload Date: {editingFile.uploadDate}</Typography>
                    <Typography>Current Status: {editingFile.status}</Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)}>
                Close
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleEditFile}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default FileLifecyclePage;